// src/context/AuthContext.jsx
import { createContext, useContext, useEffect, useRef, useState } from "react";
import {
  GoogleAuthProvider,
  browserSessionPersistence,
  onAuthStateChanged,
  setPersistence,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../config/firebaseConfig";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setAuthenticated] = useState(false);
  const [userData, setUserData] = useState(null);
  const [userName, setUserName] = useState(null);
  const [userRole, setUserRole] = useState(null); // "User" | "Admin" | null
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(true); // start in loading state
  const [lastAllowedPath, setLastAllowedPath] = useState("/"); // voor PrivateRoute
  const logoutTimerRef = useRef(null);

  // Helper: plan/clear auto-logout timer
  const scheduleAutoLogout = (ms) => {
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    logoutTimerRef.current = setTimeout(async () => {
      try {
        await signOut(auth);
      } catch (e) {
        console.error("Auto-logout error:", e);
      }
    }, Math.max(0, ms));
  };

  const clearAutoLogout = () => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true);

      if (!user) {
        clearAutoLogout();
        setUserData(null);
        setUserRole(null);
        setUserName(null);
        setPhoto(null);
        setAuthenticated(false);
        setLoading(false);
        return;
      }

      // Sessie-check (1 uur vanaf lastSignInTime)
      const lastSignInMs = user.metadata.lastSignInTime
        ? new Date(user.metadata.lastSignInTime).getTime()
        : 0;
      const now = Date.now();
      const ONE_HOUR = 60 * 60 * 1000;

      if (now - lastSignInMs >= ONE_HOUR) {
        // direct uitloggen als de sessie al verlopen is
        try {
          await signOut(auth);
        } catch (e) {
          console.error("Signout on expired session failed:", e);
        } finally {
          setLoading(false);
        }
        return;
      }

      // schedule automatische uitlog voor resterende tijd
      scheduleAutoLogout(ONE_HOUR - (now - lastSignInMs));

      // Basis user state
      setUserData(user);
      setPhoto(user.photoURL || null);
      setAuthenticated(true);

      // Rol ophalen
      try {
        const userRef = doc(db, "users", user.email);
        const snap = await getDoc(userRef);

        if (snap.exists()) {
          const data = snap.data();
          setUserRole(data.role || null);
          setUserName(data.firstname || user.displayName || user.email);
        } else {
          // geen rol → uitloggen; router beslist wat te tonen
          console.warn("Geen rol document gevonden, uitloggen.");
          await signOut(auth);
        }
      } catch (err) {
        console.error("Rol ophalen mislukt:", err);
        // conservatief uitloggen als rollen niet te bepalen zijn
        await signOut(auth);
      } finally {
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();
      clearAutoLogout();
    };
  }, []);

  const login = async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: "select_account",
      hd: "buurtzorgnederland.com",
    });

    try {
      await setPersistence(auth, browserSessionPersistence);
      await signInWithPopup(auth, provider);
      // onAuthStateChanged rondt de rest af
    } catch (error) {
      console.error("Login mislukt:", error);
    }
  };

  const logout = async () => {
    try {
      clearAutoLogout();
      await signOut(auth);
      // onAuthStateChanged reset state
    } catch (error) {
      console.error("Logout mislukt:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        userData,
        userName,
        userRole,
        login,
        logout,
        loading,
        setLoading,
        photo,
        lastAllowedPath,
        setLastAllowedPath, // <- nodig voor PrivateRoute
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
